import { BannerSet, Client, User, BannerElement, BannerSize } from '../types';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

interface BackupData {
  version: string;
  timestamp: Date;
  clients: Client[];
  users: User[];
  metadata: {
    totalBannerSets: number;
    totalBanners: number;
    totalElements: number;
    exportedBy: string;
    environment: 'development' | 'staging' | 'production';
  };
}

interface BackupOptions {
  includeUsers: boolean;
  includeClients: boolean;
  includeBannerSets: boolean;
  includeAssets: boolean;
  compression: 'none' | 'low' | 'medium' | 'high';
  encryption: boolean;
}

// Automated backup system
export class BackupManager {
  private static instance: BackupManager;
  private backupInterval: number = 6 * 60 * 60 * 1000; // 6 hours
  private maxBackups: number = 168; // Keep 1 week of 6-hour backups
  private backupTimer: NodeJS.Timeout | null = null;

  static getInstance(): BackupManager {
    if (!BackupManager.instance) {
      BackupManager.instance = new BackupManager();
    }
    return BackupManager.instance;
  }

  // Start automated backups
  startAutomatedBackups(options: Partial<BackupOptions> = {}) {
    if (this.backupTimer) {
      clearInterval(this.backupTimer);
    }

    const defaultOptions: BackupOptions = {
      includeUsers: true,
      includeClients: true,
      includeBannerSets: true,
      includeAssets: true,
      compression: 'medium',
      encryption: false
    };

    const backupOptions = { ...defaultOptions, ...options };

    this.backupTimer = setInterval(async () => {
      try {
        await this.createAutomatedBackup(backupOptions);
        console.log('Automated backup completed successfully');
      } catch (error) {
        console.error('Automated backup failed:', error);
        // In production, you'd want to send alerts here
      }
    }, this.backupInterval);

    console.log(`Automated backups started - running every ${this.backupInterval / 1000 / 60 / 60} hours`);
  }

  // Stop automated backups
  stopAutomatedBackups() {
    if (this.backupTimer) {
      clearInterval(this.backupTimer);
      this.backupTimer = null;
      console.log('Automated backups stopped');
    }
  }

  // Create a full system backup
  async createFullBackup(
    clients: Client[],
    users: User[],
    currentUser: User,
    options: BackupOptions = {
      includeUsers: true,
      includeClients: true,
      includeBannerSets: true,
      includeAssets: true,
      compression: 'medium',
      encryption: false
    }
  ): Promise<void> {
    try {
      const timestamp = new Date();
      const backupData: BackupData = {
        version: '1.0.0',
        timestamp,
        clients: options.includeClients ? clients : [],
        users: options.includeUsers ? users : [],
        metadata: {
          totalBannerSets: clients.reduce((total, client) => total + client.bannerSets.length, 0),
          totalBanners: this.countTotalBanners(clients),
          totalElements: this.countTotalElements(clients),
          exportedBy: currentUser.name,
          environment: this.detectEnvironment()
        }
      };

      const zip = new JSZip();

      // Add main backup data
      zip.file('backup.json', JSON.stringify(backupData, null, 2));

      // Add individual client backups
      if (options.includeClients) {
        const clientsFolder = zip.folder('clients');
        for (const client of clients) {
          const clientBackup = await this.createClientBackup(client, options);
          clientsFolder?.file(`${this.sanitizeFilename(client.name)}_${client.id}.json`, 
            JSON.stringify(clientBackup, null, 2));
        }
      }

      // Add banner assets if requested
      if (options.includeAssets) {
        await this.addBannerAssets(zip, clients);
      }

      // Add system metadata
      zip.file('metadata.json', JSON.stringify({
        backupDate: timestamp.toISOString(),
        version: backupData.version,
        environment: backupData.metadata.environment,
        statistics: backupData.metadata,
        checksums: await this.generateChecksums(backupData)
      }, null, 2));

      // Add restore instructions
      zip.file('RESTORE_INSTRUCTIONS.md', this.generateRestoreInstructions());

      // Generate and download backup
      const compressionLevel = this.getCompressionLevel(options.compression);
      const zipBlob = await zip.generateAsync({ 
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: compressionLevel }
      });

      const filename = `dealercentives_backup_${timestamp.toISOString().slice(0, 19).replace(/:/g, '-')}.zip`;
      saveAs(zipBlob, filename);

      console.log(`Full backup created: ${filename}`);
    } catch (error) {
      console.error('Backup creation failed:', error);
      throw new Error(`Backup failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // Create automated backup (stored to cloud storage)
  private async createAutomatedBackup(options: BackupOptions): Promise<void> {
    // In production, this would:
    // 1. Fetch current data from your database
    // 2. Create backup
    // 3. Upload to cloud storage (AWS S3, Google Cloud Storage, etc.)
    // 4. Manage backup retention
    // 5. Send notifications on success/failure

    console.log('Creating automated backup...');
    
    // Simulate backup creation
    const backupData = {
      timestamp: new Date(),
      type: 'automated',
      options
    };

    // In production, upload to cloud storage
    await this.uploadToCloudStorage(backupData);
    
    // Clean up old backups
    await this.cleanupOldBackups();
  }

  // Create client-specific backup
  private async createClientBackup(client: Client, options: BackupOptions) {
    const clientBackup = {
      client: {
        ...client,
        bannerSets: options.includeBannerSets ? client.bannerSets : []
      },
      metadata: {
        backupDate: new Date().toISOString(),
        bannerSetCount: client.bannerSets.length,
        totalBanners: client.bannerSets.reduce((total, set) => total + set.sizes.length, 0),
        totalElements: client.bannerSets.reduce((total, set) => 
          total + set.sizes.reduce((setTotal, size) => setTotal + size.elements.length, 0), 0
        )
      }
    };

    return clientBackup;
  }

  // Add banner assets to backup
  private async addBannerAssets(zip: JSZip, clients: Client[]): Promise<void> {
    const assetsFolder = zip.folder('assets');
    
    for (const client of clients) {
      const clientFolder = assetsFolder?.folder(this.sanitizeFilename(client.name));
      
      for (const bannerSet of client.bannerSets) {
        const setFolder = clientFolder?.folder(this.sanitizeFilename(bannerSet.name));
        
        for (const size of bannerSet.sizes) {
          if (size.thumbnail) {
            // Save thumbnail
            const thumbnailData = size.thumbnail.split(',')[1]; // Remove data:image/png;base64,
            setFolder?.file(`${this.sanitizeFilename(size.name)}_thumbnail.png`, thumbnailData, { base64: true });
          }
          
          // Save banner configuration
          setFolder?.file(`${this.sanitizeFilename(size.name)}_config.json`, JSON.stringify({
            size,
            elements: size.elements,
            lastModified: size.lastModified
          }, null, 2));
        }
        
        // Save data feed if exists
        if (bannerSet.dataFeedArray.length > 0) {
          setFolder?.file('data_feed.json', JSON.stringify(bannerSet.dataFeedArray, null, 2));
        }
      }
    }
  }

  // Generate checksums for data integrity
  private async generateChecksums(data: BackupData): Promise<Record<string, string>> {
    // In production, you'd use proper cryptographic hashing
    const checksums: Record<string, string> = {};
    
    checksums.clients = this.simpleHash(JSON.stringify(data.clients));
    checksums.users = this.simpleHash(JSON.stringify(data.users));
    checksums.metadata = this.simpleHash(JSON.stringify(data.metadata));
    
    return checksums;
  }

  // Simple hash function (use crypto.subtle.digest in production)
  private simpleHash(data: string): string {
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      const char = data.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(16);
  }

  // Utility functions
  private countTotalBanners(clients: Client[]): number {
    return clients.reduce((total, client) => 
      total + client.bannerSets.reduce((setTotal, set) => setTotal + set.sizes.length, 0), 0
    );
  }

  private countTotalElements(clients: Client[]): number {
    return clients.reduce((total, client) => 
      total + client.bannerSets.reduce((setTotal, set) => 
        setTotal + set.sizes.reduce((sizeTotal, size) => sizeTotal + size.elements.length, 0), 0
      ), 0
    );
  }

  private sanitizeFilename(filename: string): string {
    return filename.toLowerCase().replace(/[^a-z0-9\-_]/g, '-').replace(/-+/g, '-');
  }

  private getCompressionLevel(compression: BackupOptions['compression']): number {
    switch (compression) {
      case 'none': return 0;
      case 'low': return 3;
      case 'medium': return 6;
      case 'high': return 9;
      default: return 6;
    }
  }

  private detectEnvironment(): 'development' | 'staging' | 'production' {
    // In production, detect based on environment variables
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
        return 'development';
      } else if (hostname.includes('staging') || hostname.includes('dev')) {
        return 'staging';
      }
    }
    return 'production';
  }

  private async uploadToCloudStorage(backupData: any): Promise<void> {
    // In production, implement actual cloud storage upload
    // AWS S3, Google Cloud Storage, Azure Blob Storage, etc.
    console.log('Uploading backup to cloud storage...', backupData);
  }

  private async cleanupOldBackups(): Promise<void> {
    // In production, implement backup retention policy
    console.log('Cleaning up old backups...');
  }

  private generateRestoreInstructions(): string {
    return `
DEALERCENTIVES BACKUP RESTORE INSTRUCTIONS
==========================================

This backup contains a complete snapshot of your DealerCentives system.

BACKUP CONTENTS:
- backup.json: Main system data (clients, users, banner sets)
- clients/: Individual client backups
- assets/: Banner thumbnails and configurations
- metadata.json: Backup metadata and checksums

RESTORE PROCEDURE:

1. PREPARATION:
   - Ensure you have admin access to the DealerCentives system
   - Create a backup of the current system before restoring
   - Verify the backup integrity using checksums in metadata.json

2. DATABASE RESTORE:
   - Import backup.json into your database
   - Verify all clients and users are restored correctly
   - Check banner sets and their configurations

3. ASSET RESTORE:
   - Upload assets from the assets/ folder to your storage system
   - Update file paths and references as needed
   - Regenerate thumbnails if necessary

4. VERIFICATION:
   - Log into the system and verify all data is present
   - Test banner editing and export functionality
   - Verify user permissions and access levels
   - Check deployment configurations

5. POST-RESTORE TASKS:
   - Update any changed API keys or configurations
   - Restart automated backup schedules
   - Notify users of the restore completion
   - Monitor system for any issues

IMPORTANT NOTES:
- Always test restores in a staging environment first
- Verify data integrity using the provided checksums
- Contact support if you encounter any issues during restore
- Keep this backup in a secure location

SUPPORT:
For assistance with backup restoration, contact:
- Technical Support: support@dealercentives.com
- Emergency Hotline: Available 24/7

Generated: ${new Date().toISOString()}
`;
  }
}

// Disaster recovery utilities
export class DisasterRecovery {
  // Create emergency backup before major operations
  static async createEmergencyBackup(
    clients: Client[],
    users: User[],
    currentUser: User,
    operation: string
  ): Promise<void> {
    const backupManager = BackupManager.getInstance();
    
    console.log(`Creating emergency backup before: ${operation}`);
    
    await backupManager.createFullBackup(clients, users, currentUser, {
      includeUsers: true,
      includeClients: true,
      includeBannerSets: true,
      includeAssets: true,
      compression: 'high',
      encryption: true
    });
  }

  // Validate system integrity
  static validateSystemIntegrity(clients: Client[], users: User[]): {
    isValid: boolean;
    issues: string[];
    warnings: string[];
  } {
    const issues: string[] = [];
    const warnings: string[] = [];

    // Check for orphaned data
    clients.forEach(client => {
      if (!client.id || !client.name || !client.email) {
        issues.push(`Client missing required fields: ${client.id || 'unknown'}`);
      }

      client.bannerSets.forEach(bannerSet => {
        if (!bannerSet.id || !bannerSet.name) {
          issues.push(`Banner set missing required fields in client ${client.name}`);
        }

        bannerSet.sizes.forEach(size => {
          if (!size.id || !size.name || size.width <= 0 || size.height <= 0) {
            issues.push(`Invalid banner size in set ${bannerSet.name}`);
          }

          size.elements.forEach(element => {
            if (!element.id || !element.type) {
              issues.push(`Invalid element in banner ${size.name}`);
            }
          });
        });
      });
    });

    // Check users
    users.forEach(user => {
      if (!user.id || !user.email || !user.name) {
        issues.push(`User missing required fields: ${user.id || 'unknown'}`);
      }

      if (!user.permissions || user.permissions.length === 0) {
        warnings.push(`User ${user.name} has no permissions assigned`);
      }
    });

    return {
      isValid: issues.length === 0,
      issues,
      warnings
    };
  }

  // System health check
  static performHealthCheck(): {
    status: 'healthy' | 'warning' | 'critical';
    checks: Array<{
      name: string;
      status: 'pass' | 'warn' | 'fail';
      message: string;
    }>;
  } {
    const checks = [
      {
        name: 'Local Storage',
        status: 'pass' as const,
        message: 'Local storage is accessible'
      },
      {
        name: 'Memory Usage',
        status: 'pass' as const,
        message: 'Memory usage within normal limits'
      },
      {
        name: 'Network Connectivity',
        status: 'pass' as const,
        message: 'Network connection is stable'
      },
      {
        name: 'Backup System',
        status: 'pass' as const,
        message: 'Automated backups are running'
      }
    ];

    // In production, implement actual health checks
    const failedChecks = checks.filter(check => check.status === 'fail').length;
    const warningChecks = checks.filter(check => check.status === 'warn').length;

    let status: 'healthy' | 'warning' | 'critical' = 'healthy';
    if (failedChecks > 0) {
      status = 'critical';
    } else if (warningChecks > 0) {
      status = 'warning';
    }

    return { status, checks };
  }
}

// Export utilities for easy access
export const createBackup = async (
  clients: Client[],
  users: User[],
  currentUser: User,
  options?: Partial<BackupOptions>
) => {
  const backupManager = BackupManager.getInstance();
  return backupManager.createFullBackup(clients, users, currentUser, {
    includeUsers: true,
    includeClients: true,
    includeBannerSets: true,
    includeAssets: true,
    compression: 'medium',
    encryption: false,
    ...options
  });
};

export const startAutomatedBackups = (options?: Partial<BackupOptions>) => {
  const backupManager = BackupManager.getInstance();
  backupManager.startAutomatedBackups(options);
};

export const stopAutomatedBackups = () => {
  const backupManager = BackupManager.getInstance();
  backupManager.stopAutomatedBackups();
};